/**
 * 四角標註(kanban 1003468):單篇上傳手動「調整邊界」動線,送件成功後把每頁的四角
 * 標註與壓縮後的原圖另外送到後端 POST /cornerlabel,用來訓練四角模型。
 *
 * 原本的送件請求(aiCorrecting)一個位元組都不動;標註是送件成功後的旁支,
 * 這裡任何失敗都只 console.warn,絕不 throw、不影響送件流程與畫面。
 *
 * 座標系 = 調整邊界視窗的畫布(canvas 寬高 = 旋轉後的來源圖,來源圖是選圖時
 * compressImageForUpload 壓過的那張),角點順序 TL、TR、BR、BL(與 /cornerdetect 回傳相同)。
 *
 * 除了 encodeCornerLabelImage 要碰 DOM(Image / canvas),其餘都是純函式;
 * 專案的 vitest 是 node 環境,DOM 的部分以注入的方式測。
 */
import type { PerspectivePoints } from "./uploadImageState";
import { normalizeRotation } from "./imageUtils";
import { scalePointToSource } from "./perspectiveScale";

/** 角點,[x, y]。 */
export type CornerXY = [number, number];

export type CornerModelStatus = "detected" | "not_found" | "failed" | "pending";

/** 自動框選(/cornerdetect)這一輪的結果。 */
export interface CornerLabelModel {
  /**
   * detected:偵測到四角;not_found:API 回應偵測不到;pending:按「下一步」時結果還沒回來;
   * failed:API 失敗、回傳格式不對,或自動框選關閉沒有送偵測(此時 ms 為 null)。
   */
  status: CornerModelStatus;
  /** 只有 detected 有值(已換算回畫布座標系),其他一律 null。 */
  corners: CornerXY[] | null;
  /** 模型角點有沒有真的擺上畫布(學生先拖過角就不套用 → false)。 */
  applied: boolean;
  /** 送 API 前的縮放比例(correctionSourceToApiImage 回傳的 scale);拿不到時 null。 */
  apiScale: number | null;
  /** 從送出到結果回來的毫秒數;還沒回來時 null。 */
  ms: number | null;
}

/** 按「下一步」那一刻抓下來的一頁標註(還不知道頁序,送出時才補 pageIndex)。 */
export interface CornerLabelDraft {
  imageWidth: number;
  imageHeight: number;
  /** 順時針 0/90/180/270,已烤進送出的圖。 */
  rotation: number;
  finalCorners: CornerXY[];
  /** 系統最後擺上畫布的四角:模型角點或 10% 預設框。 */
  initialCorners: CornerXY[];
  initialSource: "model" | "default";
  model: CornerLabelModel;
  /**
   * userAdjustedCornersRef 的值:學生按到過角點附近(mousedown / touchstart 命中角點),
   * 不一定有移動。要判斷角點是否真的被拖動過,看 adjusted。
   */
  touched: boolean;
  /** finalCorners 與 initialCorners 任一角距離超過容差(2px),即真的拖動過。 */
  adjusted: boolean;
}

/** 送出的一頁標註。 */
export interface CornerLabelEntry extends CornerLabelDraft {
  pageIndex: number;
}

/**
 * 調整邊界視窗每一「輪」的紀錄:開視窗初始化、旋轉、重設角點都是新的一輪。
 * runToken 對應 cornerDetectRunRef,偵測結果只認同一輪的。
 */
export interface CornerSession {
  runToken: number;
  imageWidth: number;
  imageHeight: number;
  initialCorners: CornerXY[];
  initialSource: "model" | "default";
  model: CornerLabelModel;
}

/** 偵測結果。failed 時可能連 scale 都還沒算出來。 */
export type CornerDetectOutcome =
  | { status: "detected"; corners: CornerXY[]; apiScale: number; ms: number }
  | { status: "not_found"; apiScale: number; ms: number }
  | { status: "failed"; apiScale: number | null; ms: number };

/** PerspectivePoints → [TL, TR, BR, BL]。 */
export function toCornerArray(points: PerspectivePoints): CornerXY[] {
  return [
    [points.topLeft.x, points.topLeft.y],
    [points.topRight.x, points.topRight.y],
    [points.bottomRight.x, points.bottomRight.y],
    [points.bottomLeft.x, points.bottomLeft.y],
  ];
}

/** 任一角距離 > tolPx 就算調整過。長度不同視為調整過。 */
export function isAdjusted(final: CornerXY[], initial: CornerXY[], tolPx = 2): boolean {
  if (final.length !== initial.length) return true;
  return final.some((point, i) => {
    const dx = point[0] - initial[i][0];
    const dy = point[1] - initial[i][1];
    return Math.hypot(dx, dy) > tolPx;
  });
}

/**
 * 新的一輪:畫布剛擺上 10% 預設框。
 * detectionRequested=false(自動框選關閉)時沒有送偵測,model 記 failed、ms null。
 */
export function startCornerSession(input: {
  runToken: number;
  imageWidth: number;
  imageHeight: number;
  defaultPoints: PerspectivePoints;
  detectionRequested: boolean;
}): CornerSession {
  return {
    runToken: input.runToken,
    imageWidth: input.imageWidth,
    imageHeight: input.imageHeight,
    initialCorners: toCornerArray(input.defaultPoints),
    initialSource: "default",
    model: {
      status: input.detectionRequested ? "pending" : "failed",
      corners: null,
      applied: false,
      apiScale: null,
      ms: null,
    },
  };
}

/**
 * 偵測結果回來:只認同一輪(runToken 相同)的,舊一輪原樣回傳 session。
 * applied 由呼叫端決定(模型角點有沒有真的擺上畫布);applied 時 initialCorners 換成模型角點。
 */
export function applyCornerDetectOutcome(
  session: CornerSession | null,
  runToken: number,
  outcome: CornerDetectOutcome,
  applied: boolean
): CornerSession | null {
  if (!session || session.runToken !== runToken) return session;
  if (outcome.status === "detected") {
    return {
      ...session,
      initialCorners: applied ? outcome.corners : session.initialCorners,
      initialSource: applied ? "model" : session.initialSource,
      model: {
        status: "detected",
        corners: outcome.corners,
        applied,
        apiScale: outcome.apiScale,
        ms: outcome.ms,
      },
    };
  }
  return {
    ...session,
    model: {
      status: outcome.status,
      corners: null,
      applied: false,
      apiScale: outcome.apiScale,
      ms: outcome.ms,
    },
  };
}

/**
 * 偵測到四角(API 回傳 corners 非 null):換算回畫布座標系後記進這一輪。
 * 角點不是 4 點或換算後有非有限數字 → 這一輪整個作廢回 null(整頁不送;
 * 畫布上可能已擺上這組角點,標註會跟畫布不一致,寧可不記)。
 * 舊一輪(runToken 不同)原樣回傳 session。
 */
export function applyDetectedCorners(
  session: CornerSession | null,
  runToken: number,
  rawCorners: ReadonlyArray<readonly [number, number]>,
  input: { apiScale: number; ms: number; applied: boolean }
): CornerSession | null {
  if (!session || session.runToken !== runToken) return session;
  const corners: CornerXY[] | null =
    Array.isArray(rawCorners) && rawCorners.length === 4
      ? rawCorners.map((point) => {
          const p = scalePointToSource(point, input.apiScale);
          return [p.x, p.y] as CornerXY;
        })
      : null;
  if (!isValidCornerQuad(corners)) return null;
  return applyCornerDetectOutcome(
    session,
    runToken,
    { status: "detected", corners, apiScale: input.apiScale, ms: input.ms },
    input.applied
  );
}

/** 按「下一步」:這一輪的紀錄 + 當下的角點,組成一頁標註。 */
export function buildCornerLabelDraft(input: {
  session: CornerSession;
  finalPoints: PerspectivePoints;
  rotation: number;
  touched: boolean;
}): CornerLabelDraft {
  const { session } = input;
  const finalCorners = toCornerArray(input.finalPoints);
  return {
    imageWidth: session.imageWidth,
    imageHeight: session.imageHeight,
    rotation: normalizeRotation(input.rotation),
    finalCorners,
    initialCorners: session.initialCorners,
    initialSource: session.initialSource,
    model: { ...session.model },
    touched: input.touched,
    adjusted: isAdjusted(finalCorners, session.initialCorners),
  };
}

/** collectCornerLabelEntries 需要的 group 欄位;TFile 泛型讓測試不綁 DOM 的 File。 */
export interface CornerLabelGroup<TFile> {
  id: string;
  originalFile: TFile;
  cornerLabel?: CornerLabelDraft;
}

/**
 * 從 processedGroups 挑出有標註的頁。pageIndex 照 processedGroups 的頁序,
 * 與 originFiles(collectOriginSourcesWithRotation:略過 null、一個 group 一格)一致。
 */
export function collectCornerLabelEntries<TFile>(
  groups: ReadonlyArray<CornerLabelGroup<TFile> | null | undefined>
): Array<{ file: TFile; label: CornerLabelEntry }> {
  const entries: Array<{ file: TFile; label: CornerLabelEntry }> = [];
  let pageIndex = 0;
  for (const group of groups || []) {
    if (!group) continue;
    if (group.cornerLabel) {
      entries.push({ file: group.originalFile, label: { pageIndex, ...group.cornerLabel } });
    }
    pageIndex += 1;
  }
  return entries;
}

/** 重新編碼的結果。 */
export interface EncodedCornerLabelImage {
  blob: Blob;
  width: number;
  height: number;
}

export const CORNER_LABEL_ENCODE_TIMEOUT_MS = 10000;

/**
 * 用 canvas 重新編成 JPEG q0.92(不帶 EXIF、PNG 也轉 JPEG),旋轉方式與
 * buildRotatedSource 相同(順時針,90/270 互換寬高)。失敗或逾時回 null。
 */
export function encodeCornerLabelImage(
  file: Blob,
  rotation: number,
  quality = 0.92,
  timeoutMs = CORNER_LABEL_ENCODE_TIMEOUT_MS
): Promise<EncodedCornerLabelImage | null> {
  const angle = normalizeRotation(rotation);
  if (angle % 90 !== 0) return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;
    const url = URL.createObjectURL(file);
    const finish = (result: EncodedCornerLabelImage | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(result);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);

    const img = new Image();
    img.onload = () => {
      if (settled) return; // 已逾時
      let canvas: HTMLCanvasElement | null = null;
      // 用完就把 canvas 縮成 0,手機上不留 backing store
      const releaseCanvas = () => {
        if (canvas) {
          canvas.width = 0;
          canvas.height = 0;
        }
      };
      try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        const swap = angle === 90 || angle === 270;
        canvas = document.createElement("canvas");
        canvas.width = swap ? h : w;
        canvas.height = swap ? w : h;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          releaseCanvas();
          finish(null);
          return;
        }
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((angle * Math.PI) / 180);
        ctx.drawImage(img, -w / 2, -h / 2);
        const width = canvas.width;
        const height = canvas.height;
        canvas.toBlob(
          (blob) => {
            releaseCanvas();
            finish(blob ? { blob, width, height } : null);
          },
          "image/jpeg",
          quality
        );
      } catch {
        releaseCanvas();
        finish(null);
      }
    };
    img.onerror = () => finish(null);
    img.src = url;
  });
}

/** 單頁圖檔上限(後端同一上限)。 */
export const CORNER_LABEL_MAX_IMAGE_BYTES = 2 * 1024 * 1024;
/** pageIndex 上限(後端 le=3)。 */
export const CORNER_LABEL_MAX_PAGE_INDEX = 3;

/** 剛好 4 點、每個座標都是有限數字。 */
export function isValidCornerQuad(corners: unknown): corners is CornerXY[] {
  return (
    Array.isArray(corners) &&
    corners.length === 4 &&
    corners.every(
      (point) =>
        Array.isArray(point) &&
        point.length === 2 &&
        Number.isFinite(point[0]) &&
        Number.isFinite(point[1])
    )
  );
}

/**
 * 送出前的單頁檢查(後端整包驗證,任一頁不合格整筆 422 → 前端先把不合格的頁剔掉)。
 * 回傳不合格的原因;合格回 null。重複 pageIndex 由呼叫端判斷。
 */
export function validateCornerLabelEntry(label: CornerLabelEntry): string | null {
  if (
    !Number.isInteger(label.pageIndex) ||
    label.pageIndex < 0 ||
    label.pageIndex > CORNER_LABEL_MAX_PAGE_INDEX
  ) {
    return "pageIndex 超出 0～3";
  }
  if (!isValidCornerQuad(label.finalCorners)) return "finalCorners 不是 4 個有限座標";
  if (!isValidCornerQuad(label.initialCorners)) return "initialCorners 不是 4 個有限座標";
  const { status, corners } = label.model;
  if (status === "detected") {
    if (!isValidCornerQuad(corners)) return "model.corners 不是 4 個有限座標";
  } else if (corners !== null) {
    return "model.status 不是 detected 卻帶了 corners";
  }
  return null;
}

/**
 * 逐頁重新編碼(逐張 await,不同時 decode 多張),編出寬高與記錄不一致或編碼失敗就丟掉該頁。
 * 回傳的 images[i] 對應 labels[i]。
 */
export async function prepareCornerLabelUpload<TFile>(
  entries: Array<{ file: TFile; label: CornerLabelEntry }>,
  encode: (file: TFile, rotation: number) => Promise<EncodedCornerLabelImage | null>
): Promise<{ images: Array<{ pageIndex: number; blob: Blob }>; labels: CornerLabelEntry[] }> {
  const images: Array<{ pageIndex: number; blob: Blob }> = [];
  const labels: CornerLabelEntry[] = [];
  const seen = new Set<number>();
  for (const { file, label } of entries) {
    const invalid = validateCornerLabelEntry(label);
    if (invalid) {
      console.warn("[cornerlabel] 標註不合格,略過這一頁", { pageIndex: label.pageIndex, reason: invalid });
      continue;
    }
    if (seen.has(label.pageIndex)) {
      console.warn("[cornerlabel] pageIndex 重複,略過這一頁", { pageIndex: label.pageIndex });
      continue;
    }
    let encoded: EncodedCornerLabelImage | null = null;
    try {
      encoded = await encode(file, label.rotation);
    } catch (error) {
      console.warn("[cornerlabel] 重新編碼失敗,略過這一頁", { pageIndex: label.pageIndex, error });
      continue;
    }
    if (!encoded) {
      console.warn("[cornerlabel] 重新編碼失敗,略過這一頁", { pageIndex: label.pageIndex });
      continue;
    }
    if (encoded.width !== label.imageWidth || encoded.height !== label.imageHeight) {
      console.warn("[cornerlabel] 編出的寬高與記錄不一致,略過這一頁", {
        pageIndex: label.pageIndex,
        encoded: [encoded.width, encoded.height],
        recorded: [label.imageWidth, label.imageHeight],
      });
      continue;
    }
    if (encoded.blob.size > CORNER_LABEL_MAX_IMAGE_BYTES) {
      console.warn("[cornerlabel] 圖檔超過 2MB,略過這一頁", {
        pageIndex: label.pageIndex,
        size: encoded.blob.size,
      });
      continue;
    }
    seen.add(label.pageIndex);
    images.push({ pageIndex: label.pageIndex, blob: encoded.blob });
    labels.push(label);
  }
  return { images, labels };
}

/** multipart:ticketId、studentId、labelImages(第 i 個對應 labels[i])、labels(JSON 字串)。 */
export function buildCornerLabelFormData(input: {
  ticketId: string;
  studentId: string;
  images: Array<{ pageIndex: number; blob: Blob }>;
  labels: CornerLabelEntry[];
}): FormData {
  const data = new FormData();
  data.append("ticketId", input.ticketId);
  data.append("studentId", input.studentId);
  input.images.forEach(({ pageIndex, blob }) => {
    const file = new File([blob], `corner-label-${pageIndex}.jpg`, { type: "image/jpeg" });
    data.append("labelImages", file);
  });
  data.append("labels", JSON.stringify({ version: 1, labels: input.labels }));
  return data;
}

/**
 * 送件成功後送出四角標註。呼叫端 `void` 呼叫、不 await;這裡吞掉所有錯誤,只 console.warn。
 * encode / post 可注入(測試用)。
 */
export async function sendCornerLabels<TFile extends Blob>(input: {
  ticketId: string;
  studentId: string;
  entries: Array<{ file: TFile; label: CornerLabelEntry }>;
  post: (data: FormData) => Promise<unknown>;
  encode?: (file: TFile, rotation: number) => Promise<EncodedCornerLabelImage | null>;
}): Promise<void> {
  try {
    if (input.entries.length === 0) return;
    const { images, labels } = await prepareCornerLabelUpload(
      input.entries,
      input.encode ?? ((file, rotation) => encodeCornerLabelImage(file, rotation))
    );
    if (labels.length === 0) return;
    const data = buildCornerLabelFormData({
      ticketId: input.ticketId,
      studentId: input.studentId,
      images,
      labels,
    });
    await input.post(data);
  } catch (error) {
    console.warn("[cornerlabel] 四角標註送出失敗(不影響批改):", error);
  }
}
